import {
  AccountLinkInfo,
  AccountLinkSummary,
  CreatedAccountLink,
  EventAssignee,
  SpentAccountLink,
  User,
  type UpdateUserRequest,
  type UserRole,
} from '@bartizo/shared';
import { z } from 'zod';
import { apiRequest } from './client.js';

/** Dashboard accounts (knowledge doc sections 15 and 17; P8.4). */

export const listUsers = (): Promise<User[]> => apiRequest('/users', { schema: z.array(User) });

export const listAccountLinks = (): Promise<AccountLinkSummary[]> =>
  apiRequest('/users/links', { schema: z.array(AccountLinkSummary) });

/** Operators read this too: the people an event can be assigned to. */
export const listAssignees = (): Promise<EventAssignee[]> =>
  apiRequest('/users/assignees', { schema: z.array(EventAssignee) });

export const inviteUser = (email: string, role: UserRole): Promise<CreatedAccountLink> =>
  apiRequest('/users', { method: 'POST', body: { email, role }, schema: CreatedAccountLink });

export const createResetLink = (userId: string): Promise<CreatedAccountLink> =>
  apiRequest(`/users/${encodeURIComponent(userId)}/reset-link`, {
    method: 'POST',
    schema: CreatedAccountLink,
  });

export const revokeAccountLink = (id: string): Promise<AccountLinkSummary> =>
  apiRequest(`/users/links/${encodeURIComponent(id)}`, {
    method: 'DELETE',
    schema: AccountLinkSummary,
  });

export const updateUser = (userId: string, patch: UpdateUserRequest): Promise<User> =>
  apiRequest(`/users/${encodeURIComponent(userId)}`, {
    method: 'PATCH',
    body: patch,
    schema: User,
  });

/** Signed out: what a link is for, before a password is chosen. */
export const inspectAccountLink = (token: string): Promise<AccountLinkInfo> =>
  apiRequest('/auth/account-link/inspect', {
    method: 'POST',
    body: { token },
    schema: AccountLinkInfo,
  });

export const spendAccountLink = (
  token: string,
  password: string,
  timezone?: string,
): Promise<SpentAccountLink> =>
  apiRequest('/auth/account-link', {
    method: 'POST',
    body: { token, password, ...(timezone === undefined ? {} : { timezone }) },
    schema: SpentAccountLink,
  });

/**
 * The link a person is given: this dashboard's own address, the token after `#`
 * so no browser ever sends it in a request line.
 */
export const accountLinkUrl = (token: string): string =>
  `${window.location.origin}/account-link#${token}`;
